import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SerializedSocialChannel } from '../types';
import { xMessages } from '../xMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './PromoteChannels.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelStatusMarkProps {
  channel: SerializedSocialChannel;
  /** An X login on a plan without X: "Needs Pro" (design x-connector b2, case 6). */
  needsPro?: boolean;
}

/**
 * What this channel can do today, in one line.
 *
 * A handle is what makes a channel usable, so a row that says `manual` with no
 * handle is still offline — the mark reads the two together. A channel the
 * platform dropped says so too: it keeps its identity and needs the login back,
 * not a first login. A disconnect by the artist clears that identity, so such a
 * channel reads Offline or Manual, never "Needs reconnect".
 */
const ChannelStatusMarkComponent: React.FC<ChannelStatusMarkProps> = ({ channel, needsPro = false }) => {
  const intl = useIntl();
  const hasHandle = channel.handle !== null && channel.handle !== '';

  if (needsPro) {
    return (
      <span className={clsx(styles.status, styles.statusPending)} data-testid="channel-status-needs-pro">
        ◐ {intl.formatMessage(xMessages.needsProMark)}
      </span>
    );
  }

  if (channel.status === 'connected' && channel.syncStatus !== 'reauthRequired') {
    return (
      <span className={clsx(styles.status, styles.statusLive)}>
        ● {intl.formatMessage({ id: 'app.sml.promote.channel.mark.auto', defaultMessage: 'Auto-posting' })}
      </span>
    );
  }

  // The daily read can find the ended sign-in before a publish does: the mark says the same thing.
  if (channel.syncStatus === 'reauthRequired' || (channel.status === 'disconnected' && channel.connection !== null)) {
    return (
      <span className={clsx(styles.status, styles.statusPending)}>
        ◐ {intl.formatMessage({ id: 'app.sml.promote.channel.mark.reconnect', defaultMessage: 'Needs reconnect' })}
      </span>
    );
  }

  if (!hasHandle || channel.status === 'disconnected') {
    return (
      <span className={clsx(styles.status, styles.statusOffline)}>
        ○ {intl.formatMessage({ id: 'app.sml.promote.channel.mark.offline', defaultMessage: 'Offline' })}
      </span>
    );
  }

  if (channel.status === 'pendingApproval') {
    return (
      <span className={clsx(styles.status, styles.statusPending)}>
        ◐{' '}
        {intl.formatMessage({
          id: 'app.sml.promote.channel.mark.pending',
          defaultMessage: 'Approval pending',
        })}
      </span>
    );
  }

  return (
    <span className={clsx(styles.status, styles.statusManual)}>
      ● {intl.formatMessage({ id: 'app.sml.promote.channel.mark.manual', defaultMessage: 'Manual' })}
    </span>
  );
};

export const ChannelStatusMark = TypedMemo(ChannelStatusMarkComponent);
